import React, { useContext as useContextCore, useMemo } from 'react'
import type { UseListResult } from '@ztechapps/common.hook'
import { useListWithSelection } from '@ztechapps/common.hook'
import { type ListFieldConfig, type UseListWithSelection } from '@ztechapps/common.hook'
import type { PurchaseOrder } from '@ztechapps/common.contract'
import { usePurchaseOrderList } from '@ztechapps/purchase-order.common'
import cloneDeep from 'lodash/cloneDeep'
import type { TableHeight } from '@ztechapps/webui.data-tools'
import type { PurchaseOrderSelectListComponents } from './components'
import { defaultComponents } from './components'
import assign from 'lodash/assign'
import { purchaseOrderListFieldConfig } from '../PurchaseOrderList/constants'

export type PurchaseOrderSelectListProviderProps = {
  isMultiSelect?: boolean
  children: React.ReactNode
  tableHeight?: TableHeight
  onSelect?: (purchaseOrders: PurchaseOrder[]) => void
  saveText?: string
  components?: PurchaseOrderSelectListComponents
}

export type PurchaseOrderSelectListContext = {
  purchaseOrderList: UseListResult<PurchaseOrder>
  listWithSelection: UseListWithSelection<PurchaseOrder>
  listFieldConfig: ListFieldConfig
  storageKey: string
} & Omit<PurchaseOrderSelectListProviderProps, 'children'>

const context = React.createContext<PurchaseOrderSelectListContext>({} as PurchaseOrderSelectListContext)
const CoreProvider = context.Provider

export const PurchaseOrderSelectListProvider = ({
  isMultiSelect = false,
  tableHeight = '50vh',
  children,
  components,
  ...props
}: PurchaseOrderSelectListProviderProps) => {
  const storageKey = 'purchase-order-select-list'

  const mergedComponents = assign({}, defaultComponents, components)

  const updatedListConfig = useMemo<ListFieldConfig>(() => {
    //we need to select which field to be default show
    const updated = cloneDeep(purchaseOrderListFieldConfig)
    updated.purchaseDate.isVisible = false
    updated.estimatedArrivalDate.isVisible = false
    updated.status.isVisible = false
    updated.netAmount.isVisible = false
    updated.paymentStatus.isVisible = false
    updated.remarks.isVisible = false

    return updated
  }, [])

  const purchaseOrderList = usePurchaseOrderList({
    listActionOptions: {
      storageKey,
    },
  })

  const listWithSelection = useListWithSelection(purchaseOrderList.list)

  return (
    <CoreProvider
      value={{
        ...props,
        isMultiSelect,
        purchaseOrderList,
        listFieldConfig: updatedListConfig,
        listWithSelection,
        storageKey,
        tableHeight,
        components: mergedComponents,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const usePurchaseOrderSelectListContext = () => useContextCore(context)
